import { useState, useEffect } from 'react';
import { SectionWrapper } from '../SectionWrapper';
import { Landmark } from 'lucide-react';
import { BankAccountsSection } from '../sections/BankAccountsSection';
import { BankAccountsPhase2Step1 } from '../phase2/BankAccountsPhase2Step1';
import { BankAccountsPhase2Step3 } from '../phase2/BankAccountsPhase2Step3';
import { BankAccountsPhase2Step4 } from '../phase2/BankAccountsPhase2Step4';
import { BankAccountsPhase2Step5 } from '../phase2/BankAccountsPhase2Step5';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useToast } from '@/hooks/use-toast';

const TOTAL_STEPS = 5; // Step 1: Setup, Step 2: Import & Review, Steps 3-5: Training

interface BankAccountTrainingCombinedProps {
  onComplete?: () => void | Promise<void>;
  onSkip?: () => void | Promise<void>;
  onSaveAndReturn?: () => void | Promise<void>;
}

export const BankAccountTrainingCombined = ({ onComplete, onSkip, onSaveAndReturn }: BankAccountTrainingCombinedProps) => {
  const { completeSection, state } = useOnboarding();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isWaitingForBankConnection, setIsWaitingForBankConnection] = useState(false);

  useEffect(() => {
    // Check if bank accounts setup is already completed
    const bankAccountsStatus = state.sections.bank_accounts?.status;
    if (bankAccountsStatus === 'completed' || bankAccountsStatus === 'skipped') {
      // If already completed, start at training step
      setCurrentStep(2);
    }
  }, [state.sections.bank_accounts]);

  // Watch for when bank_accounts gets completed and move to step 2
  useEffect(() => {
    const bankAccountsStatus = state.sections.bank_accounts?.status;
    if (currentStep === 1 && (bankAccountsStatus === 'completed' || bankAccountsStatus === 'skipped')) {
      // Small delay to let the UI update
      setTimeout(() => {
        setCurrentStep(2);
      }, 500);
    }
  }, [state.sections.bank_accounts, currentStep]);

  const handleNext = async () => {
    if (currentStep === 1) {
      // After setup, mark bank_accounts as completed and move to import
      try {
        await completeSection('bank_accounts');
        setCurrentStep(2);
      } catch (error) {
        console.error('Error completing bank accounts setup:', error);
      }
    } else if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      // On last step, complete the training
      if (onComplete) {
        setIsCompleting(true);
        try {
          await onComplete();
        } catch (error) {
          console.error('Error completing training:', error);
        } finally {
          setIsCompleting(false);
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    if (onSkip) {
      try {
        await onSkip();
      } catch (error) {
        console.error('Error skipping section:', error);
      }
    }
  };

  const getStepName = () => {
    switch (currentStep) {
      case 1: return 'Bank Account Setup';
      case 2: return 'Import & Review';
      case 3: return 'Train AI';
      case 4: return 'Review';
      case 5: return 'Complete';
      default: return '';
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        // Step 1 has its own SectionWrapper, so we just render it
        return <BankAccountsSection />;
      case 2:
        return (
          <BankAccountsPhase2Step1
            onNext={handleNext}
            onAccountSelected={setSelectedAccountId}
            onLoadingStateChange={setIsWaitingForBankConnection}
          />
        );
      case 3:
        return (
          <BankAccountsPhase2Step3
            onNext={handleNext}
            onPrevious={handlePrevious}
            accountId={null}
          />
        );
      case 4:
        return (
          <BankAccountsPhase2Step4
            onNext={handleNext}
            onPrevious={handlePrevious}
            accountId={null}
          />
        );
      case 5:
        return (
          <BankAccountsPhase2Step5
            onNext={handleNext}
            onPrevious={handlePrevious}
            accountId={null}
          />
        );
      default:
        return null;
    }
  };

  // Step 1 has its own wrapper, so we render it directly
  if (currentStep === 1) {
    return <BankAccountsSection />;
  }

  // Steps 2-5 use our wrapper
  return (
    <SectionWrapper
      title="Bank Account Training"
      description="Train AI on your bank account transactions."
      icon={<Landmark className="h-6 w-6" />}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSkip={handleSkip}
      onSaveAndReturn={onSaveAndReturn}
      canSkip
      isNextDisabled={isCompleting || isWaitingForBankConnection}
      nextLabel={currentStep === TOTAL_STEPS ? 'Complete' : 'Continue'}
    >
      <div className="space-y-6">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Step {currentStep} of {TOTAL_STEPS}: {getStepName()}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round((currentStep / TOTAL_STEPS) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
          
          {/* Step indicators */}
          <div className="flex justify-between mt-2">
            {['Setup', 'Import & Review', 'Train', 'Review', 'Complete'].map((label, idx) => (
              <div 
                key={label}
                className={`text-xs ${currentStep > idx + 1 ? 'text-primary font-medium' : currentStep === idx + 1 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
              >
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        {renderStep()}
      </div>
    </SectionWrapper>
  );
};

